import React, { FunctionComponent, useState, useEffect } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "./VirtualSchoolSignup.scss";
import { useNavigate } from "react-router-dom";
import images from "../../../assets/images";
import ToasterWithMessage from "../../../components/toaster";
import { useMutation, useQuery } from "@apollo/client";
import {
  VERIFY_EXISTING_STUDENT,
  GET_PROGRAMME_CATALOGUE,
  REGISTER_STUDENT_FOR_PROGRAMME,
  GET_LANDING_PAGE_COPY,
} from "../api/AdminLoginQuery";
import ButtonStyle from "../../../components/ButtonStyle/ButtonStyle";
import Select, { components } from "react-select";
import { ExpandLess, ExpandMore, InfoOutlined } from "@mui/icons-material";
import { messages, patterns, COUNTRY_PHONE_CODE, FETCH_POLICY } from "../../../utils/constants";

export interface IProgrammeItem {
  programmeId: string;
  code?: string;
  name: string;
  description?: string;
  price?: string | number;
  currency?: string;
}

const getCustomSelectStyles = (hasError?: boolean) => ({
  container: (provided: any) => ({
    ...provided,
    width: "100%",
  }),
  control: (provided: any, state: { selectProps: { menuIsOpen: boolean }; isDisabled?: boolean; isFocused?: boolean }) => ({
    ...provided,
    minHeight: "41px",
    height: "41px",
    borderRadius: state?.selectProps?.menuIsOpen ? "10px 10px 0px 0px" : "10px",
    border: hasError
      ? "1px solid #ef4444"
      : state?.isFocused && !state?.isDisabled
      ? "1px solid var(--color-steelblue)"
      : "1px solid var(--color-white)",
    backgroundColor: state?.isDisabled ? "#f3f4f6" : "var(--color-white)",
    boxShadow: "none",
    cursor: state?.isDisabled ? "not-allowed" : "pointer",
    "&:hover": {
      border: hasError
        ? "1px solid #ef4444"
        : state?.isFocused && !state?.isDisabled
        ? "1px solid var(--color-steelblue)"
        : "1px solid var(--color-white)",
    },
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    padding: "0 12px",
    height: "41px",
  }),
  placeholder: (provided: any) => ({
    ...provided,
    fontFamily: "inherit",
    fontSize: "12px",
    fontWeight: 500,
    color: "var(--color-darkgray)",
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontFamily: "inherit",
    fontSize: "12px",
    fontWeight: 500,
    color: "#111827",
  }),
  menu: (provided: any) => ({
    ...provided,
    marginTop: "0",
    borderRadius: "0 0 10px 10px",
    backgroundColor: "var(--color-white)",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    zIndex: 99,
    maxHeight: "180px",
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: "4px 0",
    maxHeight: "180px",
    overflowY: "auto",
  }),
  option: (provided: any, state: { isSelected: boolean; isFocused: boolean }) => ({
    ...provided,
    fontFamily: "inherit",
    fontSize: "12px",
    fontWeight: 400,
    color: state.isSelected ? "var(--color-white)" : "#111827",
    backgroundColor: state.isSelected
      ? "var(--color-steelblue)"
      : state.isFocused
      ? "#f3f4f6"
      : "var(--color-white)",
    padding: "8px 12px",
    cursor: "pointer",
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    padding: "0 12px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
});

const CustomDropdownIndicator = (props: any) => {
  const { selectProps } = props;
  const isMenuOpen = selectProps.menuIsOpen;
  return (
    <components.DropdownIndicator {...props}>
      {isMenuOpen ? (
        <ExpandLess style={{ color: "var(--color-darkgray)" }} fontSize="small" />
      ) : (
        <ExpandMore style={{ color: "var(--color-darkgray)" }} fontSize="small" />
      )}
    </components.DropdownIndicator>
  );
};

interface VirtualSchoolSignupProps {
  title?: string;
  loginpage?: string;
  setSignupSuccess?: (val: boolean) => void;
}

export interface IVirtualSchoolFormInput {
  hasAccount: "yes" | "no";

  username?: string;
  password?: string;

  email?: string;
  preferredEmail: string;
  preferredPhone: string;
  referralCode?: string;
  phone?: string;

  fullName: string;
  program: string;
}

const schema = yup.object().shape({
  hasAccount: yup.string().required(),
  username: yup.string().when("hasAccount", {
    is: "yes",
    then: (s) => s.required("Username is required to verify account"),
    otherwise: (s) =>
      s
        .strict()
        .trim(messages.invalidUsername)
        .required(messages.username)
        .matches(patterns.username, messages.validUsername)
        .matches(patterns.usernameStartWithAlphabets, messages.usernameStartWithAlphabets)
        .matches(patterns.consecutiveDotsDashesUnderscores, messages.noConsecutiveDotsDashesUnderscores)
        .min(6, messages.usernameMinLength)
        .max(50, messages.usernameMaxLength)
        .test("not-only-numbers", messages.invalidUsername, (value) => {
          if (value) {
            return !patterns.onlyNumbers.test(value);
          }
          return true;
        }),
  }),
  password: yup.string().when("hasAccount", {
    is: "yes",
    then: (s) => s.required("Password is required to verify account"),
    otherwise: (s) =>
      s
        .strict()
        .trim(messages.invalidPassword)
        .required(messages.password)
        .matches(patterns?.password, messages?.validPassword)
        .min(8, messages.passwordMinLength)
        .max(30, messages.passwordMaxLength),
  }),
  email: yup.string().when("hasAccount", {
    is: "no",
    then: (s) =>
      s
        .required("Email is required")
        .trim()
        .test("is-valid-email", messages.validEmail, function (value) {
          const { path, createError } = this;
          if (value && value.length > 0) {
            return patterns.email.test(value) || createError({ path, message: messages.validEmail });
          }
          return true;
        }),
    otherwise: (s) => s.optional().nullable(),
  }),
  preferredEmail: yup
    .string()
    .trim()
    .required("Email is required")
    .test("is-valid-preferredEmail", messages.validEmail, function (value) {
      const { path, createError } = this;
      if (value && value.length > 0) {
        return patterns.email.test(value) || createError({ path, message: messages.validEmail });
      }
      return true;
    }),
  referralCode: yup.string().trim().optional(),
  preferredPhone: yup
    .string()
    .required("Contact number is required")
    .test("is-valid-preferredPhone", function (value) {
      const { path, createError } = this;
      if (!value) return true;
      
      if (!patterns.onlyNumbers.test(value)) {
        return createError({ path, message: "Contact number must contain only numbers" });
      }

      if (value.startsWith("0")) {
        if (value.length !== 10) {
          return createError({ path, message: "Contact number starting with 0 must be exactly 10 digits" });
        }
      } else {
        if (value.length !== 9) {
          return createError({ path, message: "Contact number must be exactly 9 digits" });
        }
      }
      return true;
    }),
  phone: yup.string().when("hasAccount", {
    is: "no",
    then: (s) =>
      s
        .optional()
        .nullable()
        .test("is-valid-phoneNumber", messages.validPhoneNumber, function (value) {
          const { path, createError } = this;
          if (!value) return true;
          const startsWithPlus = value.startsWith("+");
          if (!patterns.phoneNumber.test(value)) {
            return createError({ path, message: messages.validPhoneNumber });
          }
          if (startsWithPlus && value.length > 20) {
            return createError({ path, message: messages.phoneNumberLength });
          } else if (!startsWithPlus && value.length > 16) {
            return createError({ path, message: messages.phoneNumberLength16 });
          }
          return true;
        }),
    otherwise: (s) => s.optional().nullable(),
  }),

  fullName: yup.string().when("hasAccount", {
    is: "no",
    then: (s) =>
      s
        .required(messages?.fullname)
        .trim()
        .max(100, "Full name should not exceed 100 characters"),
    otherwise: (s) => s.optional().nullable(),
  }),
  program: yup.string().required("Program is required"),
});

const VirtualSchoolSignup: FunctionComponent<VirtualSchoolSignupProps> = ({
  title,
  loginpage,
  setSignupSuccess,
}) => {
  const navigate = useNavigate();
  const { backArrow, eyeon, eyeoff } = images;
  const { success, error } = ToasterWithMessage();

  // Additional component states
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

  useEffect(() => {
    const resetScroll = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      const containers = document.querySelectorAll(".landingPageContainer, .adminSignInPageOuterCtn, .virtualSchoolPage, #root");
      containers.forEach((el) => {
        if (el) el.scrollTop = 0;
      });
    };
    resetScroll();
    const t1 = setTimeout(resetScroll, 50);
    const t2 = setTimeout(resetScroll, 150);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const [verifyExistingStudent, { loading: isVerifying }] = useMutation(VERIFY_EXISTING_STUDENT);
  const [registerStudentForProgramme, { loading: isRegistering }] = useMutation(REGISTER_STUDENT_FOR_PROGRAMME);
  const { data: programmeData } = useQuery(GET_PROGRAMME_CATALOGUE);
  const { data: landingPageCopyData } = useQuery(GET_LANDING_PAGE_COPY, {
    variables: { pageKey: "virtual_school" },
    fetchPolicy: FETCH_POLICY,
  });
  const landingPageCopyContent = landingPageCopyData?.landingPageCopy?.content;
  const programDates =
    landingPageCopyContent?.dates ||
    process.env.REACT_APP_VIRTUAL_SCHOOL_PROGRAM_DATES ||
    "";
  const supportPhone =
    landingPageCopyContent?.supportPhone ||
    process.env.REACT_APP_SUPPORT_PHONE ||
    "";
  const tollFreePhone =
    landingPageCopyContent?.tollFreePhone ||
    process.env.REACT_APP_SUPPORT_TOLL_FREE ||
    "";

  const programmeList: IProgrammeItem[] = programmeData?.programmeCatalogue || [];

  const methods = useForm<IVirtualSchoolFormInput>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      hasAccount: "yes",
      username: "",
      password: "",
      email: "",
      preferredEmail: "",
      preferredPhone: "",
      referralCode: "",
      phone: "",
      fullName: "",
      program: "",
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    control,
    reset,
    formState: { errors },
  } = methods;

  const handleRadioChange = (choice: "yes" | "no") => {
    reset({
      hasAccount: choice,
      username: "",
      password: "",
      email: "",
      preferredEmail: "",
      preferredPhone: "",
      referralCode: "",
      phone: "",
      fullName: "",
      program: "",
    });
    setIsVerified(false);
  };

  const hasAccountChoice = watch("hasAccount");

  const usernameVal = watch("username");
  const passwordVal = watch("password");

  React.useEffect(() => {
    if (isVerified) {
      setIsVerified(false);
    }
  }, [hasAccountChoice, usernameVal, passwordVal]);

  React.useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsRedirecting(false);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  const handleVerifyAccount = async () => {
    const uVal = getValues("username");
    const pVal = getValues("password");

    if (!uVal || !pVal) {
      error("Please enter both Username and Password to verify.");
      return;
    }

    try {
      const response = await verifyExistingStudent({
        variables: {
          input: {
            userName: uVal,
            passWord: pVal,
          },
        },
      });

      const result = response?.data?.verifyExistingStudent;

      if (result?.verified) {
        if (result?.alreadyInVirtualClassroom) {
          setIsVerified(false);
          error(result?.message || "This account is already registered in Katon Virtual Vacation School.");
        } else {
          setIsVerified(true);
          success(result?.message || "Account verified successfully!");
          if (result?.fullName) {
            setValue("fullName", result.fullName);
          }
          if (result?.email) {
            setValue("email", result.email);
          }
          if (result?.phoneNumber) {
            setValue("phone", result?.phoneNumber);
          }
        }
      } else {
        setIsVerified(false);
        error(result?.message || "Verification failed. Please check your username and password.");
      }
    } catch (err: any) {
      setIsVerified(false);
      error(err?.message || "Failed to verify account. Please try again.");
    }
  };

  const onSubmit = async (data: IVirtualSchoolFormInput) => {
    const isExisting = hasAccountChoice === "yes";

    if (isExisting && !isVerified) {
      error("Please verify your existing Katon account before continuing.");
      return;
    }

    const inputPayload: any = {
      programmeId: data.program,
      isExistingUser: isExisting,
      userName: data.username,
      passWord: data.password,
      communicationEmail: data.preferredEmail,
      communicationPhone: data.preferredPhone,
      consentAccepted: true,
    };

    if (data.referralCode) {
      inputPayload.referralCode = data.referralCode;
    }

    if (!isExisting) {
      inputPayload.fullName = data.fullName;
      inputPayload.email = data.email;
      inputPayload.phoneNumber = data.phone;
    }

    try {
      const response = await registerStudentForProgramme({
        variables: {
          input: inputPayload,
        },
      });

      const result = response?.data?.registerStudentForProgramme;

      if (result) {
        if (result.tokens) {
          localStorage.setItem("vs_temp_tokens", JSON.stringify(result.tokens));
        }
        const savedUser = result.userName || data.username;
        if (savedUser) {
          localStorage.setItem("registered_username", savedUser);
        }

        if (result.paymentLink) {
          success("Registration complete. Redirecting you to payment gateway...");
          setIsRedirecting(true);
          reset();
          setIsVerified(false);
          setTimeout(() => {
            window.location.href = result.paymentLink;
          }, 1500);
          return;
        }

        success(result.message || "Registration submitted successfully!");
        reset();
        setIsVerified(false);

        if (setSignupSuccess) {
          setSignupSuccess(true);
        }
      }
    } catch (err: any) {
      error(err?.message || "Failed to submit registration. Please try again.");
    }
  };

  return (
    <FormProvider {...methods}>
      <div className="virtualSchoolRegistrationCtn">
        {/* Header Back Button & Title */}
        <div className="vsRegHeader">
          <div className="vsRegBackCol" onClick={() => navigate("/virtual-school")}>
            <img src={backArrow} alt="back" className="vsRegBackIcon" />
          </div>
          <div className="vsRegTitleCol">
            <h2 className="vsRegTitle">Katon Virtual Vacation School Registration</h2>
            <p className="vsRegSubtitle">
              Complete your registration to enroll in your preferred programme and gain access to live classes, learning resources and assessments.
            </p>
          </div>
        </div>

        {/* Main Registration Form */}
        <form className="vsRegCard" onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          {/* Program Information notice */}
          <div className="vsProgramInfoNotice">
            <div className="vsProgramInfoHeader">
              <h4 className="vsProgramInfoTitle">Below are the details for the classes:</h4>
            </div>
            <ol className="vsProgramInfoList">
              <li>
                Program Dates: <strong>{programDates}</strong>
              </li>
              <li>All classes will be hosted Virtually</li>
              <li>The lesson plans will be provided to students & parents / guardians</li>
              <li>Weekly attendance records of students will be sent to parents / guardians every Friday</li>
              <li>Check-in exams will be conducted to assess students' progress, strengths and weaknesses</li>
            </ol>
          </div>

          {/* Support / Contact Us block */}
          <div className="vsSupportInfoNotice">
            <InfoOutlined fontSize="small" className="vsSupportInfoIcon" />
            <span>
              For support or inquiries, contact us at <strong>{supportPhone}</strong> or call our Toll-Free center at <strong>{tollFreePhone}</strong>
            </span>
          </div>

          {/* Account Type Selection */}
          <div className="vsRegSection">
            <label className="vsSectionLabel">How would you like to continue?</label>
            <div className="vsRadioGroup">
              <label className={`vsRadioOption ${hasAccountChoice === "yes" ? "active" : ""}`}>
                <input
                  type="radio"
                  value="yes"
                  {...register("hasAccount", {
                    onChange: (e) => handleRadioChange(e.target.value as "yes" | "no"),
                  })}
                />
                <span className="vsRadioText">I already have a katon account</span>
              </label>

              <label className={`vsRadioOption ${hasAccountChoice === "no" ? "active" : ""}`}>
                <input
                  type="radio"
                  value="no"
                  {...register("hasAccount", {
                    onChange: (e) => handleRadioChange(e.target.value as "yes" | "no"),
                  })}
                />
                <span className="vsRadioText">I am new to Katon</span>
              </label>
            </div>
          </div>

          {/* Verify Account Group (Shown if user has katon account) */}
          {hasAccountChoice === "yes" && (
            <div className="vsRegSection">
              <h4 className="vsSectionTitle">
                Verify Account <span className="vsReqStar">*</span>
              </h4>
              <div className="vsGrid2">
                <div className="vsField">
                  <input
                    type="text"
                    placeholder="Username"
                    autoComplete="off"
                    {...register("username")}
                    className={`vsInput ${errors.username ? "inputError" : ""}`}
                  />
                  {errors.username && <span className="vsErrMsg">{errors.username.message}</span>}
                </div>
                <div className="vsField vsPasswordWrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    autoComplete="new-password"
                    {...register("password")}
                    className={`vsInput ${errors.password ? "inputError" : ""}`}
                  />
                  <span
                    className="vsEyeIcon"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <img src={showPassword ? eyeon : eyeoff} alt="toggle password" />
                  </span>
                  {errors.password && <span className="vsErrMsg">{errors.password.message}</span>}
                </div>
              </div>
              <div className={`vsVerifyBtnRow ${isVerified ? "isVerified" : ""}`}>
                <ButtonStyle
                  text={isVerifying ? "Verifying..." : isVerified ? "Verified!" : "Verify Account"}
                  onClick={handleVerifyAccount}
                  disableButton={isVerifying || isVerified || isRedirecting}
                />
              </div>
            </div>
          )}

          {/* Student Information Group */}
          <div className="vsRegSection">
            <h4 className="vsSectionTitle">
              Student Information <span className="vsReqStar">*</span>
            </h4>

            {hasAccountChoice === "yes" && (
              <>
                <div className="vsGrid1">
                  <div className="vsField">
                    <input
                      type="text"
                      placeholder="Full Name"
                      readOnly={true}
                      {...register("fullName")}
                      className={`vsInput ${errors.fullName ? "inputError" : ""} vsReadOnly`}
                    />
                    {errors.fullName && <span className="vsErrMsg">{errors.fullName.message}</span>}
                  </div>
                </div>

                <div className="vsGrid2">
                  <div className="vsField">
                    <input
                      type="email"
                      placeholder="Email"
                      readOnly={true}
                      autoComplete="off"
                      {...register("email")}
                      className={`vsInput ${errors.email ? "inputError" : ""} vsReadOnly`}
                    />
                    {errors.email && <span className="vsErrMsg">{errors.email.message}</span>}
                  </div>
                  <div className="vsField">
                    <input
                      type="text"
                      placeholder="Phone Number"
                      readOnly={true}
                      autoComplete="off"
                      {...register("phone")}
                      className={`vsInput ${errors.phone ? "inputError" : ""} vsReadOnly`}
                    />
                    {errors.phone && <span className="vsErrMsg">{errors.phone.message}</span>}
                  </div>
                </div>
              </>
            )}

            {hasAccountChoice === "no" && (
              <>
                <div className="vsGrid1">
                  <div className="vsField">
                    <input
                      type="text"
                      placeholder="Full Name"
                      {...register("fullName")}
                      className={`vsInput ${errors.fullName ? "inputError" : ""}`}
                    />
                    {errors.fullName && <span className="vsErrMsg">{errors.fullName.message}</span>}
                  </div>
                </div>

                <div className="vsGrid2">
                  <div className="vsField">
                    <input
                      type="text"
                      placeholder="Username"
                      autoComplete="off"
                      {...register("username")}
                      className={`vsInput ${errors.username ? "inputError" : ""}`}
                    />
                    {errors.username && <span className="vsErrMsg">{errors.username.message}</span>}
                  </div>
                  <div className="vsField vsPasswordWrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      autoComplete="new-password"
                      {...register("password")}
                      className={`vsInput ${errors.password ? "inputError" : ""}`}
                    />
                    <span
                      className="vsEyeIcon"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <img src={showPassword ? eyeon : eyeoff} alt="toggle password" />
                    </span>
                    {errors.password && <span className="vsErrMsg">{errors.password.message}</span>}
                  </div>
                </div>
                
                <div className="vsGrid2">
                  <div className="vsField">
                    <input
                      type="email"
                      placeholder="Email"
                      autoComplete="off"
                      {...register("email")}
                      className={`vsInput ${errors.email ? "inputError" : ""}`}
                    />
                    {errors.email && <span className="vsErrMsg">{errors.email.message}</span>}
                  </div>
                  <div className="vsField">
                    <input
                      type="text"
                      placeholder="Phone Number"
                      autoComplete="off"
                      {...register("phone")}
                      className={`vsInput ${errors.phone ? "inputError" : ""}`}
                    />
                    {errors.phone && <span className="vsErrMsg">{errors.phone.message}</span>}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Choose a Program */}
          <div className="vsRegSection">
            <h4 className="vsSectionTitle">
              Choose a Program <span className="vsReqStar">*</span>
            </h4>
            <div className="vsGrid2">
              <div className="vsField">
                <Controller
                  control={control}
                  name="program"
                  render={({ field }) => {
                    const progOptions = programmeList.map((prog: IProgrammeItem) => {
                      const curr = prog.currency || "GHS";
                      const priceVal = prog.price || "";
                      const val = prog.programmeId || prog.name;
                      const lbl = priceVal ? `${prog.name} (${curr} ${priceVal})` : prog.name;
                      return { value: val, label: lbl };
                    });
                    const selectedOption = progOptions.find((o) => o.value === field.value) || null;
                    return (
                      <Select
                        id="program"
                        placeholder="Program"
                        options={progOptions}
                        value={selectedOption}
                        onChange={(selected: any) => field.onChange(selected?.value || "")}
                        styles={getCustomSelectStyles(!!errors.program)}
                        components={{ DropdownIndicator: CustomDropdownIndicator }}
                        isSearchable={false}
                      />
                    );
                  }}
                />
                {errors.program && <span className="vsErrMsg">{errors.program.message}</span>}
              </div>
            </div>
          </div>

          {/* Communication Preferences */}
          <div className="vsRegSection">
            <h4 className="vsSectionTitle">
              Communication Preferences <span className="vsReqStar">*</span>
            </h4>
            <div className="vsGrid2">
              <div className="vsField">
                <input
                  type="email"
                  placeholder="Email"
                  autoComplete="off"
                  {...register("preferredEmail")}
                  className={`vsInput ${errors.preferredEmail ? "inputError" : ""}`}
                />
                {errors.preferredEmail && <span className="vsErrMsg">{errors.preferredEmail.message}</span>}
              </div>
              <div className="vsField">
                <div className={`vsPhoneInputWrapper ${errors.preferredPhone ? "inputError" : ""}`}>
                  <div className="vsPhonePrefix">{COUNTRY_PHONE_CODE}</div>
                  <input
                    type="text"
                    placeholder="Contact Number"
                    autoComplete="off"
                    {...register("preferredPhone")}
                    className="vsInput vsPhoneInput"
                  />
                </div>
                {errors.preferredPhone && <span className="vsErrMsg">{errors.preferredPhone.message}</span>}
              </div>
              <div className="vsField">
                <input
                  type="text"
                  placeholder="Referral Code"
                  autoComplete="off"
                  {...register("referralCode")}
                  className="vsInput"
                />
              </div>
            </div>

            <div className="vsInfoNotice">
              <InfoOutlined fontSize="small" className="vsInfoIcon" />
              <span>
                Provide your preferred email address and contact number to receive important updates related to your virtual learning journey.
              </span>
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="vsRegFooter">
            <ButtonStyle
              text={isRegistering || isRedirecting ? "Please wait..." : "Register and Pay"}
              type="submit"
              disableButton={isVerifying || isRegistering || isRedirecting}
            />
          </div>
        </form>
      </div>
    </FormProvider>
  );
};

export default VirtualSchoolSignup;

